import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Star } from "lucide-react";
import { PageHeader } from "@/components/molecules/PageHeader";
import { Pagination } from "@/components/molecules/Pagination";
import { Skeleton } from "@/components/atoms/Skeleton";
import { EmptyState } from "@/components/molecules/EmptyState";
import { ImageViewer } from "@/components/organisms/ImageViewer";
import { usePagedResource } from "@/hooks/usePagedResource";
import { mediaAssetsService, type MediaAssetFilter } from "@/services/mediaAssetsService";
import { useSettingsStore } from "@/store/settingsStore";
import { useCrmMappingSyncStore } from "@/store/crmMappingSyncStore";
import { useUiStore } from "@/store/uiStore";
import type { PageRequest, PageResponse } from "@/types/api";
import type { PropertyMediaAssetEntity } from "@/types/property";

export function ImagesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get("propertyId") || undefined;
  const externalPropertyId = searchParams.get("externalPropertyId") || undefined;
  const returnView = searchParams.get("returnView") === "tile" ? "tile" : "list";
  const isPropertyContext = Boolean(propertyId || externalPropertyId);

  const handleBackToProperties = useCallback(() => {
    navigate(`/properties?view=${returnView}`);
  }, [navigate, returnView]);

  const { legalEntityId, branchId } = useSettingsStore();
  const crmMappingEpoch = useCrmMappingSyncStore((s) => s.crmMappingEpoch);

  const imagesListFetcher = useCallback(
    (filter: MediaAssetFilter, page: PageRequest): Promise<PageResponse<PropertyMediaAssetEntity>> =>
      mediaAssetsService.list(filter, page),
    [crmMappingEpoch],
  );

  const list = usePagedResource<PropertyMediaAssetEntity, MediaAssetFilter>(imagesListFetcher, {
    initialPage: { page: 0, size: 24, sort: "orderIndex", direction: "asc" },
    initialFilter: {
      ...(propertyId ? { propertyId } : {}),
      ...(externalPropertyId ? { externalPropertyId } : {}),
      legalEntityId: legalEntityId ?? undefined,
      branchId: branchId || undefined
    },
  });

  const { startPageLoading, stopPageLoading } = useUiStore();

  useEffect(() => {
    if (list.loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [list.loading, startPageLoading, stopPageLoading]);

  const asString = (value: unknown): string | null => {
    if (typeof value !== "string") return null;
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  };

  // The hook already performs the initial fetch with `initialFilter`. Only call
  // setFilter when the inputs actually change from what was last fetched, so we
  // don't fire a duplicate request on mount (and stay robust to StrictMode's
  // double-invoke, since we compare values rather than a mount flag).
  const lastFilterKey = useRef(
    JSON.stringify([propertyId, externalPropertyId, legalEntityId, branchId]),
  );
  useEffect(() => {
    const key = JSON.stringify([propertyId, externalPropertyId, legalEntityId, branchId]);
    if (key === lastFilterKey.current) return;
    lastFilterKey.current = key;
    list.setFilter({
      ...(propertyId ? { propertyId } : {}),
      ...(externalPropertyId ? { externalPropertyId } : {}),
      legalEntityId: legalEntityId ?? undefined,
      branchId: branchId || undefined
    });
  }, [propertyId, externalPropertyId, legalEntityId, branchId]);

  const galleryItems = useMemo(() => {
    const items: Array<{
      id: string;
      thumbnailUrl: string;
      fullUrl: string;
      category: string;
      isMain: boolean;
    }> = [];

    (list.data?.content ?? []).forEach((asset, assetIndex) => {
      const assetId = asset.id ?? asset.externalId ?? `asset-${assetIndex}`;
      const gallery = Array.isArray(asset.galleryImages) ? asset.galleryImages : [];

      if (gallery.length === 0) {
        const fallbackUrl =
          asString(asset.internalUrl) ??
          asString(asset.internalThumbnailUrl);

        if (fallbackUrl) {
          items.push({
            id: `${assetId}-fallback`,
            thumbnailUrl: fallbackUrl,
            fullUrl: fallbackUrl,
            category: asString(asset.type) ?? "gallery",
            isMain: false,
          });
        }
        return;
      }

      gallery.forEach((raw, imageIndex) => {
        const image = (raw ?? {}) as Record<string, unknown>;
        const thumbnailUrl =
          asString(image.internalSizesThumbnailSrc) ??
          asString(image.internalSizesTileSrc) ??
          asString(image.internalSizesScreenSrc) ??
          asString(asset.internalThumbnailUrl) ??
          asString(asset.internalUrl);

        if (!thumbnailUrl) return;

        // Prefer the largest available source for the full-screen viewer.
        const fullUrl =
          asString(image.internalSizesScreenSrc) ??
          asString(image.internalSizesTileSrc) ??
          asString(asset.internalUrl) ??
          thumbnailUrl;

        items.push({
          id: `${assetId}-${asString(image.externalId) ?? imageIndex}`,
          thumbnailUrl,
          fullUrl,
          category: asString(image.category) ?? asString(asset.type) ?? "gallery",
          isMain: image.main === true,
        });
      });
    });

    // Drop repeats: when per-image sources are missing they collapse to the same
    // fallback URL, which would otherwise show the same picture multiple times in
    // the grid and the viewer.
    const seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.fullUrl)) return false;
      seen.add(item.fullUrl);
      return true;
    });
  }, [list.data?.content]);

  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // A new page should start at the top of the grid, not wherever the previous
    // page was scrolled to.
    if (gridRef.current) gridRef.current.scrollTop = 0;
  }, [list.pageRequest.page]);

  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const viewerImages = useMemo(
    () => galleryItems.map((img) => ({ id: img.id, src: img.fullUrl, alt: img.category })),
    [galleryItems],
  );

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Images"
        subtitle={
          propertyId
            ? `Images for property ${propertyId}`
            : externalPropertyId
            ? `Images for property ${externalPropertyId}`
            : `${list.data?.totalElements ?? 0} media assets from the media API.`
        }
        onBack={isPropertyContext ? handleBackToProperties : undefined}
        backAriaLabel="Back to properties"
      />
      <div className="flex-1 mx-6 mb-6 glass rounded-lg overflow-hidden flex flex-col">
        <div ref={gridRef} className="flex-1 overflow-auto p-4">
          {list.loading && list.data == null ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="aspect-[4/3] w-full" />
              ))}
            </div>
          ) : galleryItems.length === 0 ? (
            <EmptyState title="No images match" description="Try a different category." />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {galleryItems.map((img, imgIndex) => (
                <figure
                  key={img.id}
                  onClick={() => setViewerIndex(imgIndex)}
                  className="relative aspect-[4/3] rounded-md overflow-hidden bg-bridge-surface/60 border border-bridge-border/60 group cursor-pointer"
                >
                  <img
                    src={img.thumbnailUrl}
                    alt={img.category}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                  {img.isMain && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-xs bg-black/60 text-white text-[10px] uppercase tracking-wide">
                      <Star size={10} /> Main
                    </span>
                  )}
                  <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-[11px] text-white/90">
                    {img.category}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>

        <Pagination
          page={list.data?.page ?? 0}
          totalPages={list.data?.totalPages ?? 0}
          totalElements={list.data?.totalElements ?? 0}
          size={list.data?.size ?? 24}
          onPageChange={list.setPage}
        />
      </div>

      <ImageViewer
        open={viewerIndex !== null}
        images={viewerImages}
        index={viewerIndex ?? 0}
        onIndexChange={setViewerIndex}
        onClose={() => setViewerIndex(null)}
      />
    </div>
  );
}